import { createContext, useContext } from 'react';
import { useDialog } from '@admin/shared/hooks/use-dialog';
import type { DialogProps } from '@admin/shared/components/dialogs/dialog';

import type { ShowDeleteDialogType } from '@admin/shared/components/dialogs/delete-dialog';
import { useDeleteDialog } from '@admin/shared/components/dialogs/delete-dialog';

import type { ShowConfirmChangesDialogType } from '@admin/shared/components/dialogs/confirm-changes-dialog';
import { useConfirmChangesDialog } from '@admin/shared/components/dialogs/confirm-changes-dialog';

export type ShowDialogType = (opts: DialogProps) => void;

/**
 * Type of dialog context.
 */
type DialogContextType = {
  showDialog: ShowDialogType;
  showDeleteDialog: ShowDeleteDialogType;
  showConfirmChangesDialog: ShowConfirmChangesDialogType;
  handleClose: () => void;
};

/**
 * Type of component properties
 */
type DialogWrapperProps = {
  children: JSX.Element;
};

// create (react) context
const DialogContext = createContext<DialogContextType | null>(null);

// provide hook to access context
export const useDialogContext = (): DialogContextType => {
  const context = useContext(DialogContext);

  if (context == null) {
    throw new Error(
      'DialogContext is null - Did you forget to wrap your component with DialogWrapper?',
    );
  }

  return context;
};

/**
 * Wrapper for DialogContext.
 *
 * Provides dialog functionality to all children.
 *
 * @param {JSX.Element} children - child elements
 *
 * @returns {JSX.Element}
 */
export const DialogWrapper = ({ children }: DialogWrapperProps): JSX.Element => {
  const [dialog, showDialog, handleClose] = useDialog();
  const showDeleteDialog = useDeleteDialog(showDialog);
  const showConfirmChangesDialog = useConfirmChangesDialog(showDialog);

  // create context value object
  const dialogContext = {
    showDialog,
    showDeleteDialog,
    showConfirmChangesDialog,
    handleClose,
  };

  return (
    <DialogContext.Provider value={dialogContext}>
      {children}
      {dialog}
    </DialogContext.Provider>
  );
};
